import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaArrowRight, FaPaperPlane, FaPhoneAlt, FaEnvelope, FaGlobeAsia, FaBookOpen,
  FaUserCheck, FaCreditCard, FaHeadset, FaRegCommentDots,
  FaChevronDown, FaCheck, FaHandshake, FaInstagram, FaYoutube, FaFacebookF
} from "react-icons/fa";
import { contactAPI } from "../services/api";
import { toast } from "react-toastify";

// ─── INQUIRY OPTIONS WITH RICH ICONS & DESCRIPTIONS ──────────────────────────
const INQUIRY_OPTIONS = [
  {
    value: "General Inquiry",
    label: "General Inquiry",
    desc: "Platform guidance & sacred wellness overview",
    icon: FaRegCommentDots,
  },
  {
    value: "Course Information",
    label: "Course Information",
    desc: "Curriculums, live classes & teacher training",
    icon: FaBookOpen,
  },
  {
    value: "Personal Training",
    label: "Personal Training",
    desc: "1-on-1 private coaching & custom routines",
    icon: FaUserCheck,
  },
  {
    value: "Subscriptions & Billing",
    label: "Subscriptions & Billing",
    desc: "Membership tiers, upgrades & receipts",
    icon: FaCreditCard,
  },
  {
    value: "Technical Support",
    label: "Technical Support",
    desc: "Account access, video stream or app help",
    icon: FaHeadset,
  },
  {
    value: "Feedback & Collaboration",
    label: "Feedback & Collaboration",
    desc: "Suggestions, partnerships & teacher inquiries",
    icon: FaHandshake,
  },
];

// ─── DATA ────────────────────────────────────────────────────────────────────
const countries = [
  { name:"India",          city:"Gondia",   lat:16.46,  lng:72.19  },
  { name:"United States",  city:"New York", lat:35.71,  lng:-84.00 },
  { name:"Dubai",          city:"UAE",      lat:10.20,  lng:40.27  },
  { name:"United Kingdom", city:"London",   lat:51.50,  lng:-1.12  },
  { name:"Australia",      city:"Sydney",   lat:-40.86, lng:131.20 },
];

const supportItems = [
  { icon:FaPhoneAlt,  text:"9 AM – 6 PM IST" },
  { icon:FaEnvelope,  text:"Client Email Pending" },
  { icon:FaGlobeAsia, text:"Global Wellness Team" },
];

const faqData = [
  { question: "What types of yoga classes do you offer?", answer: "We offer expert-led live classes ranging from beginner, intermediate, and advanced levels, alongside specialized courses like Morning Wellness, Stress Relief, and Teacher Training." },
  { question: "Can I join a live session from any country?", answer: "Yes! Our virtual platform is designed for global access. We have active members from over 15 countries practicing daily." },
  { question: "What batch timings are available?", answer: "We have flexible schedules with morning, evening, and weekend batches. Subscribed members can choose and switch slots anytime." },
  { question: "Is there a free trial option available?", answer: "Yes, we offer a 7-day premium free trial which includes access to starter yoga courses, select live sessions, and meditation modules." },
  { question: "Can I access class recordings?", answer: "Yes! All memberships (except basic trials) include 24/7 unlimited access to recorded replays of all live sessions." },
];

const convertToPosition = (lat, lng) => ({
  left: `${((lng + 180) / 360) * 100}%`,
  top:  `${((90 - lat) / 180) * 100}%`,
});

// ─── SHARED BADGE ─────────────────────────────────────────────────────────────
const Badge = ({ label, dark = false }) => (
  <motion.div
    initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.2, duration:0.8 }}
    className={`inline-flex items-center gap-3 px-5 py-3 rounded-full border mt-8 ${dark ? "border-white/40 bg-white/20 backdrop-blur-2xl" : "bg-[#F29A0C]/10 border-[#F29A0C]/20"}`}>
    <div className={`w-2 h-2 rounded-full ${dark ? "bg-[#F29A0C]" : "bg-[#F29A0C]"}`}/>
    <span className={`tracking-[3px] uppercase text-xs font-semibold ${dark ? "text-white" : "text-[#F29A0C]"}`}>{label}</span>
  </motion.div>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const Contact = () => {
  const navigate = useNavigate();
  const contactFormSectionRef = useRef(null);
  const nameInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    inquiryType: "General Inquiry",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const [activeCountry, setActiveCountry] = useState(null);
  const [activeFaq, setActiveFaq]         = useState(0);
  const [isMobile, setIsMobile]           = useState(true);

  // Custom Inquiry Dropdown State
  const [dropdownOpen, setDropdownOpen]   = useState(false);
  const dropdownRef = useRef(null);

  const scrollToContactForm = () => {
    if (contactFormSectionRef.current) {
      contactFormSectionRef.current.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 500);
    }
  };

  const handleExplorePrograms = () => {
    navigate("/courses");
  };

  const selectedInquiryOption = useMemo(() => {
    return INQUIRY_OPTIONS.find((o) => o.value === formData.inquiryType) || INQUIRY_OPTIONS[0];
  }, [formData.inquiryType]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error("Please provide your name, email, and message.");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await contactAPI.submitQuery({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        inquiryType: formData.inquiryType,
        message: formData.message.trim()
      });
      if (res.data && res.data.success) {
        setSubmitted(true);
        toast.success(res.data.message || "Your inquiry has been submitted successfully!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          inquiryType: "General Inquiry",
          message: ""
        });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        toast.error(res.data?.message || "Failed to submit inquiry.");
      }
    } catch (err) {
      console.error("Contact query submission error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to submit inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const particles = useMemo(() => {
    return Array.from({ length: 12 }).map((_, i) => ({
      id: i,
      width: `${(i % 3) + 4}px`,
      height: `${(i % 3) + 4}px`,
      top: `${((i * 13) % 80) + 10}%`,
      left: `${((i * 19) % 80) + 10}%`,
      duration: `${5 + (i % 4)}s`,
      delay: `${i * 0.4}s`,
    }));
  }, []);

  return (
    <div className="relative overflow-hidden bg-[#FFF9F2]">
      {/* Dynamic Keyframes injected locally to ensure 60fps animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes float-particle {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.2; }
          50% { transform: translateY(-30px) translateX(12px); opacity: 0.7; }
        }
        .animate-float-particle {
          animation: float-particle var(--duration) ease-in-out infinite;
        }
      `}} />

      {/* ══════════════════════════════════════════
          HERO
      ══════════════════════════════════════════ */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Base Hero Background Layer */}
        <div className="absolute inset-0 w-full h-full">
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-[center_30%] sm:bg-center" 
            style={{ backgroundImage: "url('/assets/contact_hero_bg.jpg')" }} 
          />
          {/* Subtle dark brown overlay on background image */}
          <div className="absolute inset-0 bg-[#120700]/40 pointer-events-none" />
        </div>

        {!isMobile ? (
          <video autoPlay muted loop playsInline preload="metadata" loading="lazy"
            poster="/assets/contact_hero_bg.jpg"
            className="absolute inset-0 w-full h-full object-cover scale-[1.08]"
            style={{ willChange:"transform", transform:"translateZ(0)", backfaceVisibility:"hidden" }}>
            <source src="/assets/video19.mp4" type="video/mp4"/>
          </video>
        ) : (
          <div className="absolute inset-0 w-full h-full">
            <div 
              className="absolute inset-0 w-full h-full bg-cover bg-[center_30%] sm:bg-center" 
              style={{ backgroundImage: "url('/assets/contact_hero_bg.jpg')" }} 
            />
            {/* Subtle dark brown overlay on mobile background image */}
            <div className="absolute inset-0 bg-[#120700]/40 pointer-events-none" />
          </div>
        )}

        {/* Rich Royal Backdrop Gradients & Overlays (Balanced opacity for artwork clarity) */}
        <div className="absolute inset-0 z-10 bg-[#120700]/20 pointer-events-none" />
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-[#120700]/70 via-[#180902]/45 to-[#0D0500]/75 lg:bg-gradient-to-r lg:from-[#120700]/75 lg:via-[#180902]/45 lg:to-[#120700]/15 pointer-events-none" />
        <div className="absolute inset-0 z-10 bg-[radial-gradient(circle_at_top_left,rgba(242,154,12,0.20),transparent_25%)] pointer-events-none" />
        <div className="absolute inset-0 z-10 bg-[radial-gradient(circle_at_bottom_right,rgba(242,154,12,0.15),transparent_25%)] pointer-events-none" />
        <div className="absolute inset-0 bg-black/[0.3]"/>
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay bg-[url('https://www.transparenttextures.com/patterns/asfalt-light.png')]"/>
        <motion.div animate={{ scale:[1,1.06,1], opacity:[0.14,0.24,0.14] }} transition={{ repeat:Infinity, duration:10 }}
          className="absolute top-[-250px] right-[-180px] w-[850px] h-[850px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.18)_0%,transparent_70%)] pointer-events-none will-change-transform" style={{ transform: "translateZ(0)" }}/>
        <motion.div animate={{ rotate:360 }} transition={{ repeat:Infinity, duration:120, ease:"linear" }}
          style={{ willChange: "transform" }}
          className="absolute right-[8%] top-[8%] w-[500px] h-[500px] rounded-full border border-[#F29A0C]/10 pointer-events-none"/>
        {/* Hardware-accelerated CSS floating particles */}
        {particles.slice(0, 10).map((p) => (
          <div key={p.id}
            className="absolute rounded-full bg-white/20 animate-float-particle pointer-events-none"
            style={{
              width: p.width,
              height: p.height,
              top: p.top,
              left: p.left,
              "--duration": p.duration,
              animationDelay: p.delay,
              willChange: "transform, opacity",
            }} />
        ))}

        <div className="relative z-20 h-full flex items-center py-6">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-20 w-full">
            <div className="max-w-[780px] pt-[120px]">
              <Badge label="GLOBAL WELLNESS SUPPORT" dark/>
              <motion.h1 initial={{ opacity:0, y:40 }} animate={{ opacity:1, y:0 }} transition={{ duration:1 }}
                className="text-[46px] md:text-[64px] lg:text-[68px] leading-[1.2] tracking-[1px] font-semibold text-white mt-8"
                style={{ fontFamily:"Poppins" }}>
                Connect<br/>With Our
                <span className="text-[#F29A0C]"> Wellness</span><br/>Support Team
              </motion.h1>
              <motion.p initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ duration:1, delay:0.1 }}
                className="text-white text-[17px] md:text-[19px] leading-[38px] max-w-[680px] mt-8">
                Have questions about classes, subscriptions, or your wellness journey?
                Our support specialists and instructors are available globally to guide you anytime.
              </motion.p>
              <div className="flex flex-wrap gap-5 mt-6 py-4">
                <motion.button 
                  type="button"
                  onClick={scrollToContactForm}
                  whileHover={{ y:-4, scale:1.03 }} 
                  whileTap={{ scale:0.97 }}
                  className="group relative overflow-hidden rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF7A00] text-white px-8 py-4 text-[15px] font-semibold shadow-[0_25px_80px_rgba(242,154,12,0.28)] cursor-pointer border-none"
                >
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-all duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"/>
                  <span className="relative z-20 flex items-center gap-3">Contact Support<FaArrowRight/></span>
                </motion.button>
                <motion.button 
                  type="button"
                  onClick={handleExplorePrograms}
                  whileHover={{ y:-4, scale:1.03 }} 
                  whileTap={{ scale:0.97 }}
                  className="rounded-full border border-white/40 bg-white/20 hover:bg-white/30 backdrop-blur-2xl text-white px-8 py-4 text-[15px] font-medium cursor-pointer transition-all border-none"
                >
                  Explore Programs
                </motion.button>
              </div>

              {/* Official Social Links in Hero Section */}
              <div className="flex items-center gap-3 mt-6">
                <span className="text-white/70 text-xs tracking-[2px] uppercase">Official Socials:</span>
                <a href="https://www.instagram.com/divyyog.foundation" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white hover:bg-[#F29A0C] transition-all">
                  <FaInstagram size={16} />
                </a>
                <a href="https://www.youtube.com/@divyogafoundation1169" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white hover:bg-[#F29A0C] transition-all">
                  <FaYoutube size={16} />
                </a>
                <a href="https://www.facebook.com/people/Divyyogfoundation-Gondia/61558710747446/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white hover:bg-[#F29A0C] transition-all">
                  <FaFacebookF size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          CONTACT FORM
      ══════════════════════════════════════════ */}
      <section id="contact-form" ref={contactFormSectionRef} className="relative min-h-screen overflow-hidden py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF8EE] via-[#FFE2B3] to-[#FFF7EA]"/>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,154,12,0.20),transparent_32%)]"/>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(242,154,12,0.16),transparent_32%)]"/>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.30),transparent_45%)]"/>
        <motion.div animate={{ scale:[1,1.08,1], opacity:[0.18,0.28,0.18] }} transition={{ repeat:Infinity, duration:10 }}
          className="absolute top-[-260px] left-[-220px] w-[850px] h-[850px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.22)_0%,transparent_70%)] pointer-events-none will-change-transform" style={{ transform: "translateZ(0)" }}/>
        <motion.div animate={{ scale:[1,1.05,1], opacity:[0.10,0.18,0.10] }} transition={{ repeat:Infinity, duration:12 }}
          className="absolute bottom-[-260px] right-[-220px] w-[750px] h-[750px] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.3)_0%,transparent_70%)] pointer-events-none will-change-transform" style={{ transform: "translateZ(0)" }}/>
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay bg-[url('https://www.transparenttextures.com/patterns/asfalt-light.png')]"/>
        <motion.div animate={{ rotate:360 }} transition={{ repeat:Infinity, duration:120, ease:"linear" }}
          style={{ willChange: "transform" }}
          className="absolute top-[10%] right-[8%] w-[520px] h-[520px] rounded-full border border-[#F29A0C]/10 pointer-events-none"/>
        <motion.div animate={{ rotate:-360 }} transition={{ repeat:Infinity, duration:140, ease:"linear" }}
          style={{ willChange: "transform" }}
          className="absolute bottom-[5%] left-[4%] w-[420px] h-[420px] rounded-full border border-[#F29A0C]/10 pointer-events-none"/>
        {/* Hardware-accelerated CSS floating particles */}
        {particles.map((p) => (
          <div key={p.id}
            className="absolute rounded-full bg-white/30 animate-float-particle pointer-events-none"
            style={{
              width: p.width,
              height: p.height,
              top: p.top,
              left: p.left,
              "--duration": p.duration,
              animationDelay: p.delay,
              willChange: "transform, opacity",
            }} />
        ))}

        <div className="relative z-20 max-w-[1550px] mx-auto px-6 lg:px-20">
          
          {/* Section Header */}
          <div className="max-w-[850px] mx-auto text-center mb-20">
            <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} transition={{ duration:0.8 }}
              className="inline-flex items-center gap-3 rounded-full border border-white/40 bg-white/20 backdrop-blur-2xl px-6 py-3">
              <div className="w-2 h-2 rounded-full bg-[#F29A0C]"/>
              <span className="text-[10px] tracking-[4px] uppercase font-semibold text-[#333333]">GLOBAL WELLNESS CONCIERGE</span>
            </motion.div>
            <motion.h2 initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }} transition={{ duration:0.9 }}
              className="text-[42px] md:text-[58px] lg:text-[66px] leading-[1.2] tracking-[-1px] font-semibold text-[#111111] mt-6"
              style={{ fontFamily:"Poppins" }}>
              Speak With Our<br/>
              <span className="text-[#F29A0C]">Wellness Team</span>
            </motion.h2>
            <motion.p initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} transition={{ duration:1, delay:0.1 }}
              className="text-[#555555] text-[17px] md:text-[19px] leading-[38px] mt-6 max-w-[700px] mx-auto">
              Connect with our specialists for yoga guidance, subscriptions, teacher training, and personalized support designed for your wellness transformation.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1.0fr] gap-16 items-start">
            
            {/* LEFT PANEL: Premium Floating Glass Contact Form */}
            <motion.div 
              initial={{ opacity:0, x:-40 }} 
              whileInView={{ opacity:1, x:0 }} 
              transition={{ duration:0.8 }}
              className="relative overflow-hidden rounded-[44px] border border-white/40 bg-white/25 backdrop-blur-[30px] p-8 md:p-10 shadow-[0_40px_140px_rgba(0,0,0,0.06)]"
            >
              <div className="absolute top-[-100px] right-[-100px] w-[260px] h-[260px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.15)_0%,transparent_70%)] pointer-events-none"/>
              <div className="relative z-20">
                <Badge label="SEND A MESSAGE"/>
                <h3 className="text-[28px] md:text-[34px] leading-tight font-semibold text-[#111111] mt-4" style={{ fontFamily:"Poppins" }}>
                  Write to us
                </h3>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5 mt-8 relative z-20">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input 
                    ref={nameInputRef}
                    type="text" 
                    required 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full Name"
                    className="w-full h-14 rounded-2xl border border-white/30 bg-white/40 backdrop-blur-xl px-5 text-[#111111] placeholder:text-[#666666] outline-none focus:border-[#F29A0C] focus:bg-white/60 transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.02)]"
                  />
                  <input 
                    type="email" 
                    required 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address"
                    className="w-full h-14 rounded-2xl border border-white/30 bg-white/40 backdrop-blur-xl px-5 text-[#111111] placeholder:text-[#666666] outline-none focus:border-[#F29A0C] focus:bg-white/60 transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.02)]"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input 
                    type="text" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number (Optional)"
                    className="w-full h-14 rounded-2xl border border-white/30 bg-white/40 backdrop-blur-xl px-5 text-[#111111] placeholder:text-[#666666] outline-none focus:border-[#F29A0C] focus:bg-white/60 transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.02)]"
                  />
                  {/* Custom Inquiry Dropdown */}
                  <div className="relative z-30" ref={dropdownRef}>
                    <button
                      type="button"
                      onClick={() => setDropdownOpen((prev) => !prev)}
                      className="w-full h-14 rounded-2xl border border-white/30 bg-white/40 backdrop-blur-xl px-5 text-[#111111] outline-none focus:border-[#F29A0C] transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.02)] flex items-center justify-between cursor-pointer group hover:bg-white/60"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-xl bg-[#F29A0C]/15 border border-[#F29A0C]/30 flex items-center justify-center text-[#F29A0C] shrink-0 transition-transform group-hover:scale-105">
                          {React.createElement(selectedInquiryOption.icon, { size: 14 })}
                        </div>
                        <span className="text-xs sm:text-sm font-semibold text-[#111111] truncate">
                          {selectedInquiryOption.label}
                        </span>
                      </div>
                      <motion.div
                        animate={{ rotate: dropdownOpen ? 180 : 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="text-[#666666] group-hover:text-[#F29A0C] shrink-0 ml-2"
                      >
                        <FaChevronDown size={12} />
                      </motion.div>
                    </button>

                    {/* Animated Dropdown Menu */}
                    <AnimatePresence>
                      {dropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -6, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -6, scale: 0.98 }}
                          transition={{ duration: 0.18 }}
                          className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 rounded-2xl bg-white/95 backdrop-blur-2xl border border-[#F4D9B5]/90 shadow-[0_20px_50px_rgba(242,154,12,0.15)] p-2 max-h-[280px] overflow-y-auto space-y-1"
                        >
                          {INQUIRY_OPTIONS.map((opt) => {
                            const isSelected = formData.inquiryType === opt.value;
                            const Icon = opt.icon;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => {
                                  setFormData((prev) => ({ ...prev, inquiryType: opt.value }));
                                  setDropdownOpen(false);
                                }}
                                className={`w-full p-2.5 rounded-xl text-left flex items-center justify-between gap-3 transition-all cursor-pointer ${
                                  isSelected
                                    ? "bg-gradient-to-r from-[#F29A0C] to-[#E97818] text-white shadow-sm"
                                    : "hover:bg-[#FFF8EE] text-[#333333] hover:text-[#111111]"
                                }`}
                              >
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div
                                    className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                                      isSelected
                                        ? "bg-white/20 text-white"
                                        : "bg-[#FFF8EE] border border-[#F4D9B5] text-[#F29A0C]"
                                    }`}
                                  >
                                    <Icon size={12} />
                                  </div>
                                  <div className="min-w-0">
                                    <div className={`text-xs font-bold truncate ${isSelected ? "text-white" : "text-[#111111]"}`}>
                                      {opt.label}
                                    </div>
                                    <div className={`text-[10px] truncate ${isSelected ? "text-white/80" : "text-[#777777]"}`}>
                                      {opt.desc}
                                    </div>
                                  </div>
                                </div>
                                {isSelected && <FaCheck size={11} className="text-white shrink-0 mr-1" />}
                              </button>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <textarea 
                  rows="4" 
                  required
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us how we can help you..."
                  className="w-full rounded-[28px] border border-white/30 bg-white/40 backdrop-blur-xl p-5 text-[#111111] placeholder:text-[#666666] outline-none resize-none focus:border-[#F29A0C] focus:bg-white/60 transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.02)]"
                />

                <motion.button 
                  whileHover={!isSubmitting && !submitted ? { y: -4, scale: 1.02 } : {}} 
                  whileTap={!isSubmitting && !submitted ? { scale: 0.98 } : {}} 
                  type="submit"
                  disabled={isSubmitting || submitted}
                  className={`group relative overflow-hidden rounded-full w-full h-14 font-semibold text-[15px] flex items-center justify-center transition-all duration-500 shadow-[0_20px_50px_rgba(242,154,12,0.2)] cursor-pointer ${
                    submitted 
                      ? "bg-green-600 text-white shadow-[0_20px_50px_rgba(22,163,74,0.3)]" 
                      : "bg-gradient-to-r from-[#F29A0C] to-[#FF7A00] text-white"
                  }`}
                >
                  {/* Confetti Blast */}
                  {submitted && (
                    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-full">
                      {Array.from({ length: 18 }).map((_, i) => (
                        <motion.span
                          key={i}
                          initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
                          animate={{ 
                            x: (Math.random() - 0.5) * 220, 
                            y: -80 - Math.random() * 100, 
                            scale: 0.2 + Math.random() * 0.8,
                            rotate: Math.random() * 360,
                            opacity: 0
                          }}
                          transition={{ duration: 1.5, ease: "easeOut" }}
                          className="absolute w-2 h-2 rounded-full"
                          style={{ 
                            background: ["#F29A0C", "#FFD166", "#34A853", "#FFFFFF", "#FF5252"][i % 5],
                            left: "50%",
                            top: "50%",
                          }}
                        />
                      ))}
                    </div>
                  )}

                  {/* Shine effect on hover */}
                  {!isSubmitting && !submitted && (
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-all duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"/>
                  )}

                  {isSubmitting ? (
                    <div className="flex items-center gap-3">
                      <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Sending Message...</span>
                    </div>
                  ) : submitted ? (
                    <motion.div 
                      initial={{ scale: 0.8 }} 
                      animate={{ scale: 1 }} 
                      className="flex items-center gap-2"
                    >
                      <span className="text-lg">✓</span>
                      <span>Message Sent Successfully!</span>
                    </motion.div>
                  ) : (
                    <span className="flex items-center gap-3">Send Message<FaPaperPlane className="text-sm" /></span>
                  )}
                </motion.button>
              </form>

              {/* Support contact items at bottom of form */}
              <div className="relative z-20 flex flex-wrap justify-between items-center gap-5 border-t border-black/10 mt-8 pt-6">
                <div className="flex flex-wrap items-center gap-5">
                  {supportItems.map(({ icon:Icon, text }, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/40 flex items-center justify-center text-[#F29A0C] shadow-sm">
                        <Icon/>
                      </div>
                      <span className="text-[#333333] text-sm font-medium">{text}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-[#666666] text-xs font-medium uppercase tracking-[1px]">Social:</span>
                  <a href="https://www.instagram.com/divyyog.foundation" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-xl bg-white/50 border border-black/10 flex items-center justify-center text-[#222222] hover:text-white hover:bg-[#F29A0C] transition-all shadow-sm">
                    <FaInstagram size={15} />
                  </a>
                  <a href="https://www.youtube.com/@divyogafoundation1169" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-9 h-9 rounded-xl bg-white/50 border border-black/10 flex items-center justify-center text-[#222222] hover:text-white hover:bg-[#F29A0C] transition-all shadow-sm">
                    <FaYoutube size={15} />
                  </a>
                  <a href="https://www.facebook.com/people/Divyyogfoundation-Gondia/61558710747446/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-xl bg-white/50 border border-black/10 flex items-center justify-center text-[#222222] hover:text-white hover:bg-[#F29A0C] transition-all shadow-sm">
                    <FaFacebookF size={14} />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* RIGHT PANEL: Interactive sliding FAQ Accordion */}
            <motion.div 
              initial={{ opacity:0, x:40 }} 
              whileInView={{ opacity:1, x:0 }} 
              transition={{ duration:0.8 }}
              className="space-y-4"
            >
              <div className="mb-6">
                <Badge label="FREQUENTLY ASKED QUESTIONS" />
                <h3 className="text-[28px] md:text-[34px] leading-tight font-semibold text-[#111111] mt-4" style={{ fontFamily:"Poppins" }}>
                  Common Queries
                </h3>
              </div>

              {faqData.map((faq, i) => {
                const isOpen = activeFaq === i;
                return (
                  <div 
                    key={i} 
                    className={`rounded-3xl border border-white/50 bg-white/40 backdrop-blur-xl overflow-hidden transition-all duration-500 shadow-sm ${
                      isOpen ? "ring-1 ring-[#F29A0C]/50 bg-white/70 shadow-[0_15px_45px_rgba(242,154,12,0.06)]" : "hover:bg-white/60"
                    }`}
                  >
                    <button
                      onClick={() => setActiveFaq(isOpen ? null : i)}
                      className="w-full flex items-center justify-between p-6 text-left outline-none cursor-pointer"
                    >
                      <span className="font-semibold text-[16px] md:text-[18px] text-[#111111] pr-4">{faq.question}</span>
                      <motion.span 
                        animate={{ rotate: isOpen ? 180 : 0 }} 
                        className={`text-xs transition-colors ${isOpen ? "text-[#F29A0C]" : "text-gray-500"}`}
                      >
                        ▼
                      </motion.span>
                    </button>
                    
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                          <div className="px-6 pb-6 pt-1 border-t border-black/5 text-[#555555] text-sm md:text-[15px] leading-[26px]">
                            {faq.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          WORLD MAP
      ══════════════════════════════════════════ */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF8EE] via-[#FFFDF8] to-[#FFFFFF]"/>
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ repeat: Infinity, duration: 12 }}
          className="absolute top-[-250px] left-[-220px] w-[850px] h-[850px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.08)_0%,transparent_70%)] pointer-events-none will-change-transform"
          style={{ transform: "translateZ(0)" }}
        />

        <div className="relative z-20 max-w-[1650px] mx-auto px-4 sm:px-6 lg:px-20">
          <div className="text-center max-w-[900px] mx-auto">
            <Badge label="WORLDWIDE WELLNESS NETWORK"/>
            <h2
              className="text-[36px] sm:text-[42px] md:text-[58px] leading-[1.0] tracking-[2px] font-semibold text-[#111111] mt-6"
              style={{ fontFamily: "Poppins" }}
            >
              A Global<br/>Wellness Ecosystem
            </h2>
          </div>

          <div className="relative overflow-hidden rounded-[28px] sm:rounded-[42px] border border-[#F4D9B5] bg-white/70 backdrop-blur-2xl mt-12 sm:mt-20 min-h-[320px] sm:min-h-[500px] lg:min-h-[700px] shadow-[0_40px_140px_rgba(0,0,0,0.05)]">
            <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(0,0,0,0.3)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.3)_1px,transparent_1px)] [background-size:80px_80px]"/>
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg"
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-[0.12]"
            />

            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1000 600" preserveAspectRatio="xMidYMid meet">
              <line x1="180" y1="220" x2="660" y2="320" stroke="rgba(242,154,12,0.25)" strokeWidth="1.5"/>
              <line x1="470" y1="180" x2="660" y2="320" stroke="rgba(242,154,12,0.25)" strokeWidth="1.5"/>
              <line x1="660" y1="320" x2="760" y2="390" stroke="rgba(242,154,12,0.25)" strokeWidth="1.5"/>
            </svg>

            {countries.map((country, i) => {
              const pos = convertToPosition(country.lat, country.lng);
              return (
                <motion.button
                  key={i}
                  whileHover={{ scale: 1.08 }}
                  onClick={() => setActiveCountry(country)}
                  className="absolute z-20"
                  style={{ top: pos.top, left: pos.left, transform: "translate(-50%,-50%)" }}
                >
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-[#F29A0C]/30 animate-ping"/>
                    <div className="relative w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[#F29A0C] border border-white shadow-[0_0_30px_rgba(242,154,12,0.8)]"/>
                    {/* Label: hidden on mobile, shown md+ */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="hidden md:block absolute left-7 top-[-14px] whitespace-nowrap rounded-full border border-white/60 bg-white/85 backdrop-blur-2xl px-4 py-2 shadow-[0_10px_40px_rgba(0,0,0,0.08)]"
                    >
                      <span className="text-[#111111] text-xs sm:text-sm font-medium">{country.name}</span>
                    </motion.div>
                  </div>
                </motion.button>
              );
            })}

            <AnimatePresence>
              {activeCountry && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="absolute bottom-4 sm:bottom-8 left-0  -translate-x-1/2 z-30 rounded-[20px] sm:rounded-[30px] border border-[#F4D9B5] bg-white/85 backdrop-blur-3xl px-5 sm:px-8 py-5 sm:py-7 shadow-[0_30px_120px_rgba(0,0,0,0.08)] w-[calc(100%-2rem)] sm:w-auto sm:min-w-[320px] max-w-[480px]"
                >
                  <div className="flex items-center justify-between gap-6 sm:gap-10">
                    <div>
                      <span className="text-[#F29A0C] uppercase tracking-[3px] text-[10px] font-semibold">GLOBAL WELLNESS HUB</span>
                      <h3 className="text-[#111111] text-[22px] sm:text-[28px] font-semibold mt-3 sm:mt-4">{activeCountry.name}</h3>
                      <p className="text-[#666666] text-sm mt-2 sm:mt-3">{activeCountry.city}</p>
                    </div>
                    <button
                      onClick={() => setActiveCountry(null)}
                      className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-[#FFF4E3] text-[#F29A0C] font-semibold text-lg"
                    >
                      ×
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;